/* XV Capital Partners — Lightweight i18n
 * --------------------------------------
 * Provides a `useI18n()` hook with a `t(path)` translator and a
 * `lang` / `setLang()` getter-setter. Language is persisted to
 * localStorage and synced with <html lang="…">.
 */
import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";

import { translations } from "@/lib/translations";

const I18nCtx = createContext(null);
const STORAGE_KEY = "xv_lang";
const DEFAULT_LANG = "fr";

function detectInitialLang() {
    if (typeof window === "undefined") return DEFAULT_LANG;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "fr" || stored === "en") return stored;
    // Auto-detect from browser language on first visit
    const nav = (window.navigator?.language || "").toLowerCase();
    if (nav.startsWith("en")) return "en";
    return DEFAULT_LANG;
}

function getByPath(obj, path) {
    return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}

export function I18nProvider({ children }) {
    const [lang, setLangState] = useState(detectInitialLang);

    useEffect(() => {
        if (typeof document !== "undefined") {
            document.documentElement.setAttribute("lang", lang);
        }
        if (typeof window !== "undefined") {
            window.localStorage.setItem(STORAGE_KEY, lang);
        }
    }, [lang]);

    const setLang = useCallback((next) => {
        if (next === "fr" || next === "en") setLangState(next);
    }, []);

    const t = useCallback(
        (path, fallback) => {
            const dict = translations[lang] || translations[DEFAULT_LANG];
            const value = getByPath(dict, path);
            if (value !== undefined && value !== null) return value;
            // Fallback: try default lang, then provided fallback, then raw path
            const fb = getByPath(translations[DEFAULT_LANG], path);
            if (fb !== undefined && fb !== null) return fb;
            return fallback ?? path;
        },
        [lang]
    );

    const ctxValue = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

    return <I18nCtx.Provider value={ctxValue}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
    const ctx = useContext(I18nCtx);
    if (!ctx) {
        // Fallback identity if provider missing (e.g., cockpit / client pages)
        return {
            lang: DEFAULT_LANG,
            setLang: () => {},
            t: (_, fb) => fb ?? "",
        };
    }
    return ctx;
}
